import React, { useContext, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ToastContext } from 'app/context/ToastContext';
import { deleteImportedProvisionFile, getImportedProvisionFiles } from 'app/api/provisionApi';
import { formatDateFromInstant } from 'app/shared/util/date-utils';

const ImportedProvisionFileTable: React.FC = () => {
  const { showSuccess, showError } = useContext(ToastContext);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setLoading(true);
    getImportedProvisionFiles()
      .then(response => setFiles(response.data))
      .catch(() => showError('Chyba', 'Nastala chyba pri načítaní súborov.'))
      .finally(() => setLoading(false));
  };

  const confirmDelete = file => {
    setSelectedFile(file);
    setDeleteDialog(true);
  };

  const handleDelete = () => {
    setLoading(true);
    deleteImportedProvisionFile(selectedFile.id)
      .then(() => {
        setFiles(prev => prev.filter(f => f.id !== selectedFile.id));
        setDeleteDialog(false);
        showSuccess('Úspech', `Súbor "${selectedFile.fileName}" a všetky jeho provízie boli vymazané.`);
      })
      .catch(e => {
        setDeleteDialog(false);
        showError('Chyba', e.response?.data?.detail || 'Nastala chyba pri mazaní súboru.');
      })
      .finally(() => setLoading(false));
  };

  const actionBodyTemplate = rowData => {
    return (
      <Button
        icon="pi pi-trash"
        rounded
        outlined
        severity="danger"
        disabled={rowData.usedInProvisionSheet}
        title={rowData.usedInProvisionSheet ? 'Súbor nie je možné vymazať – niektoré provízie sú zahrnuté v províznych listoch' : 'Vymazať súbor'}
        onClick={() => confirmDelete(rowData)}
      />
    );
  };

  const usedBadge = rowData => {
    if (rowData.usedInProvisionSheet) {
      return <span style={{ color: 'red', fontWeight: 'bold' }}>Áno</span>;
    }
    return <span style={{ color: 'green' }}>Nie</span>;
  };

  const deleteDialogFooter = (
    <>
      <Button style={{ backgroundColor: 'gray' }} label="Nie" icon="pi pi-times" outlined onClick={() => setDeleteDialog(false)} />
      <Button style={{ backgroundColor: '#e31818' }} label="Áno, vymazať" icon="pi pi-check" severity="danger" onClick={handleDelete} />
    </>
  );

  return (
    <div>
      <div className="card">
        <h4>Hromadné zmazanie importovaných provízií</h4>
        <p style={{ color: 'gray', marginBottom: '1rem' }}>
          Tu môžete vymazať importované súbory spolu so všetkými províziami, ktoré obsahujú. Súbor nie je možné vymazať, ak sú niektoré
          jeho provízie zahrnuté v províznych listoch.
        </p>
        <DataTable
          stripedRows
          value={files}
          size="small"
          responsiveLayout="scroll"
          dataKey="id"
          emptyMessage="Žiadne importované súbory."
          loading={loading}
        >
          <Column field="id" header="ID" sortable />
          <Column field="fileName" header="Názov súboru" sortable />
          <Column field="insuranceCompanyName" header="Spoločnosť" sortable />
          <Column field="provisionCount" header="Počet provízií" sortable />
          <Column field="createdDate" header="Importované" sortable body={v => formatDateFromInstant(v.createdDate)} />
          <Column field="usedInProvisionSheet" header="Použité v listoch" body={usedBadge} />
          <Column body={actionBodyTemplate} header="Akcia" />
        </DataTable>
      </div>

      <Dialog
        visible={deleteDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header="Potvrdenie vymazania"
        modal
        footer={deleteDialogFooter}
        onHide={() => setDeleteDialog(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem', color: '#e31818' }} />
          {selectedFile && (
            <span>
              Naozaj chcete vymazať súbor <b>{selectedFile.fileName}</b> a všetky jeho <b>{selectedFile.provisionCount}</b> provízií? Táto
              akcia je nevratná.
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default ImportedProvisionFileTable;
